import React from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Alert, Box, Typography, CircularProgress } from "@mui/material";
import {
  fetchClassById,
  fetchSessionsForCourse,
  fetchChecklistsForCourse,
  fetchTestsForCourse,
  fetchUsers,
  fetchClassLevelContent,
  fetchSessionContent,
  fetchCourseDocumentAssignments,
  fetchChecklistsForSession,
  fetchCourseSkillSheetAssignments,
  fetchTestsForSession,
  fetchLearningPaths,
  fetchLearningPlan,
  deleteClass,
  publishClass,
  unpublishClass,
  completeClass,
  fetchInstructorDashboard,
} from "../../api/backend";
import { useTenant } from "../../contexts/TenantContext";
import useCoursePermissions from "../../hooks/useCoursePermissions";
import { setActiveTenant as setApiTenant } from "../../api/apiClient";
import ConfirmDialog from "../../components/ConfirmDialog";
import { useTheme, alpha } from "@mui/material/styles";
import ClassOverview from "../../components/classOverview/ClassOverview";
import { countActive } from "../contentIsActiveHelpers";

import RosterTab from "./RosterTab";
import CoordinatorDetailsTab from "./CoordinatorDetailsTab";
import CoordinatorInstructorsTab from "./CoordinatorInstructorsTab";
import CoordinatorSessionsTab from "./CoordinatorSessionsTab";
import CoordinatorReportsTab from "./CoordinatorReportsTab";
import SkillChecklistsTab from "./SkillChecklistsTab";
import DocumentsTab from "./DocumentsTab";
import WrittenTestsTab from "./WrittenTestsTab";
import VirtualLearningTab from "./VirtualLearningTab";

// ── Design tokens ─────────────────────────────────────────────────────────────
const D = {
  ink:   "var(--color-ink)",
  ink2:  "rgba(31,22,51,0.75)",
  ink3:  "rgba(31,22,51,0.6)",
  ink4:  "rgba(31,22,51,0.5)",
  line:  "var(--color-hairline-cloud)",
  line2: "var(--color-hairline-cloud)",
  surface:  "var(--color-surface-canvas-light)",
  surface2: "var(--color-surface-press-light)",
  surface3: "var(--color-surface-press-stronger)",
  green:    "var(--color-success)",
  green50:  "var(--color-success-bg)",
  green100: "var(--color-success-bg)",
  amber:    "var(--color-warn)",
  amber50:  "var(--color-warn-bg)",
  amber100: "var(--color-warn-bg)",
  live:     "#c23d68",
  live50:   "color-mix(in srgb, var(--color-accent-pink) 10%, transparent)",
  live100:  "color-mix(in srgb, var(--color-accent-pink) 22%, transparent)",
  danger:    "var(--color-danger)",
  danger50:  "var(--color-danger-bg)",
  danger100: "var(--color-danger-bg)",
  shadowSm: "var(--shadow-card-sm)",
  primary: "var(--color-primary)",
};

// ── Category color mapping ────────────────────────────────────────────────────
function getCatColors(category = "", tPrimary = "var(--color-primary)") {
  const c = category.toLowerCase();
  if (c.includes("fire") || c.includes("wildland"))
    return { bg: D.live50, color: D.live, border: D.live100 };
  if (c.includes("ems") || c.includes("medical"))
    return { bg: D.green50, color: D.green, border: D.green100 };
  if (c.includes("hazmat") || c.includes("haz"))
    return { bg: D.amber50, color: D.amber, border: D.amber100 };
  return { bg: alpha(tPrimary, 0.06), color: tPrimary, border: alpha(tPrimary, 0.10) };
}

// ── Inline SVG helper ─────────────────────────────────────────────────────────
function SvgIcon({ size = 14, children, ...rest }) {
  return (
    <Box
      component="svg"
      width={size} height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...rest}
    >
      {children}
    </Box>
  );
}

// ── Tab definitions ───────────────────────────────────────────────────────────
const TABS = [
  {
    route: "",
    label: "Roster",
    icon: (
      <SvgIcon>
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
      </SvgIcon>
    ),
  },
  {
    route: "details",
    label: "Details",
    icon: (
      <SvgIcon>
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="8" x2="12" y2="12"/>
        <line x1="12" y1="16" x2="12.01" y2="16"/>
      </SvgIcon>
    ),
  },
  {
    route: "instructors",
    label: "Instructors",
    icon: (
      <SvgIcon>
        <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
      </SvgIcon>
    ),
  },
  {
    route: "sessions",
    label: "Sessions",
    icon: (
      <SvgIcon>
        <rect x="3" y="4" width="18" height="18" rx="2"/>
        <line x1="16" y1="2" x2="16" y2="6"/>
        <line x1="8" y1="2" x2="8" y2="6"/>
        <line x1="3" y1="10" x2="21" y2="10"/>
      </SvgIcon>
    ),
  },
  {
    route: "documents",
    label: "Documents",
    icon: (
      <SvgIcon>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <polyline points="14 2 14 8 20 8"/>
        <line x1="16" y1="13" x2="8" y2="13"/>
        <line x1="16" y1="17" x2="8" y2="17"/>
      </SvgIcon>
    ),
  },
  {
    route: "skill-sheets",
    label: "Skill Sheets",
    icon: (
      <SvgIcon>
        <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/>
        <rect x="9" y="3" width="6" height="4" rx="1"/>
        <polyline points="9 12 11 14 15 10"/>
      </SvgIcon>
    ),
  },
  {
    route: "tests",
    label: "Tests",
    icon: (
      <SvgIcon>
        <rect x="3" y="3" width="18" height="18" rx="2"/>
        <path d="M9 9h6"/>
        <path d="M9 13h6"/>
        <path d="M9 17h4"/>
      </SvgIcon>
    ),
  },
  {
    route: "virtual-learning",
    label: "Virtual Learning",
    icon: (
      <SvgIcon>
        <line x1="8" y1="6" x2="21" y2="6"/>
        <line x1="8" y1="12" x2="21" y2="12"/>
        <line x1="8" y1="18" x2="21" y2="18"/>
        <line x1="3" y1="6" x2="3.01" y2="6"/>
        <line x1="3" y1="12" x2="3.01" y2="12"/>
        <line x1="3" y1="18" x2="3.01" y2="18"/>
      </SvgIcon>
    ),
  },
  {
    route: "reports",
    label: "Reports",
    icon: (
      <SvgIcon>
        <line x1="18" y1="20" x2="18" y2="10"/>
        <line x1="12" y1="20" x2="12" y2="4"/>
        <line x1="6" y1="20" x2="6" y2="14"/>
      </SvgIcon>
    ),
  },
];

// ── Page ──────────────────────────────────────────────────────────────────────
export default function CoordinatorCourseDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { effectiveUser, activeTenantId } = useTenant();
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const tSecondary = theme.palette.secondary.main;

  const [course, setCourse] = React.useState(null);
  const [sessions, setSessions] = React.useState([]);
  const [checklists, setChecklists] = React.useState([]);
  const [tests, setTests] = React.useState([]);
  const [courseInstructors, setCourseInstructors] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState("");
  const [documentsCount, setDocumentsCount] = React.useState(0);
  const [checklistsActiveCount, setChecklistsActiveCount] = React.useState(0);
  const [testsActiveCount, setTestsActiveCount] = React.useState(0);
  const [vlpCount, setVlpCount] = React.useState(0);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [publishOpen, setPublishOpen] = React.useState(false);
  const [completeOpen, setCompleteOpen] = React.useState(false);
  // Enrolled students not signed off as passed, read when the complete dialog opens; null while unknown.
  const [unsignedStudents, setUnsignedStudents] = React.useState(null);
  const completeCourseId = course?.id;
  React.useEffect(() => {
    if (!completeOpen || !completeCourseId) return undefined;
    let active = true;
    setUnsignedStudents(null);
    // The warning is a courtesy: if the roster can not be read the class can still be completed.
    Promise.resolve()
      .then(() => fetchInstructorDashboard(completeCourseId))
      .then((rows) => {
        if (!active || !Array.isArray(rows)) return;
        setUnsignedStudents(rows
          .filter((r) => r.status !== "WAITLISTED" && r.passed !== true)
          .map((r) => r.user?.name || r.user?.username || `Student ${r.userId}`));
      })
      .catch(() => { if (active) setUnsignedStudents(null); });
    return () => { active = false; };
  }, [completeOpen, completeCourseId]);

  const { isCourseCoordinator, isAdmin } = useCoursePermissions(effectiveUser, course);

  // Tab badges must reflect class-level + session-level + bank-assigned totals
  // (not just class-level), and must be correct without visiting the tab —
  // so counts are fetched independently here rather than relying solely on
  // each tab's own internal state.
  React.useEffect(() => {
    if (!id) {
      setDocumentsCount(0);
      setChecklistsActiveCount(0);
      setTestsActiveCount(0);
      return;
    }

    let cancelled = false;

    const loadCounts = async () => {
      const sessionList = sessions || [];
      const sessionsById = Object.fromEntries(
        sessionList.map((s) => [String(s.id), s]),
      );
      const activeOpts = { sessionsById };

      const [classDocs, bankDocs, ...sessionDocsLists] = await Promise.all([
        fetchClassLevelContent(id).catch(() => []),
        fetchCourseDocumentAssignments(id).catch(() => []),
        ...sessionList.map((s) => fetchSessionContent(s.id).catch(() => [])),
      ]);

      const [bankChecklists, ...sessionChecklistsLists] = await Promise.all([
        fetchCourseSkillSheetAssignments(id).catch(() => []),
        ...sessionList.map((s) =>
          fetchChecklistsForSession(id, s.id).catch(() => []),
        ),
      ]);

      // Tests have no bank-assignment join like documents/checklists —
      // "From Bank" clones a bank test directly into the class-level list
      // (see AddFromBankModal's CONFIG.tests.assign), so only session tests
      // are counted here on top of the class-level `tests` state.
      const sessionTestsLists = await Promise.all(
        sessionList.map((s) => fetchTestsForSession(id, s.id).catch(() => [])),
      );

      if (cancelled) return;

      const flat = (lists) => lists.flatMap((l) => (Array.isArray(l) ? l : []));

      setDocumentsCount(
        countActive(
          [...classDocs, ...bankDocs, ...flat(sessionDocsLists)],
          activeOpts,
        ),
      );
      setChecklistsActiveCount(
        countActive(
          [...checklists, ...bankChecklists, ...flat(sessionChecklistsLists)],
          activeOpts,
        ),
      );
      setTestsActiveCount(
        countActive([...tests, ...flat(sessionTestsLists)], activeOpts),
      );
    };

    loadCounts();

    return () => {
      cancelled = true;
    };
  }, [id, sessions, checklists, tests]);

  // Virtual Learning Path item count — sums items across all learning paths
  // for the course (existing frontend fetch functions from VirtualLearningTab.js).
  React.useEffect(() => {
    if (!id) {
      setVlpCount(0);
      return;
    }
    let cancelled = false;
    (async () => {
      const paths = await fetchLearningPaths(id).catch(() => []);
      const plans = await Promise.all(
        (Array.isArray(paths) ? paths : []).map((p) =>
          fetchLearningPlan(id, p.id).catch(() => null),
        ),
      );
      if (cancelled) return;
      const total = plans.reduce(
        (sum, plan) =>
          sum + (Array.isArray(plan?.items) ? plan.items.length : 0),
        0,
      );
      setVlpCount(total);
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  React.useEffect(() => {
    let cancelled = false;

    async function loadCourse() {
      setLoadError("");
      setCourse(null);
      setSessions([]);
      setChecklists([]);
      setTests([]);
      setCourseInstructors([]);
      setLoading(true);

      try {
        const cls = await fetchClassById(id);
        if (cancelled) return;

        setCourse(cls);
        if (!cls) {
          setLoading(false);
          return;
        }

        if (cls?.tenantId) setApiTenant(String(cls.tenantId));

        const results = await Promise.allSettled([
          fetchSessionsForCourse(id),
          fetchChecklistsForCourse(id),
          fetchTestsForCourse(id),
        ]);
        if (cancelled) return;

        const [sess, chk, tsts] = results;
        setSessions(sess.status === "fulfilled" ? sess.value || [] : []);
        setChecklists(chk.status === "fulfilled" ? chk.value || [] : []);
        setTests(tsts.status === "fulfilled" ? tsts.value || [] : []);

        if (results.some((result) => result.status === "rejected")) {
          setLoadError("Some course details could not be loaded. Check your session and permissions, then refresh.");
        }
      } catch (err) {
        if (!cancelled) {
          console.error("CoordinatorCourseDetailPage load error:", err);
          setLoadError("Course details could not be loaded. Check your session and permissions, then refresh.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadCourse();

    return () => {
      cancelled = true;
    };
  }, [id]);

  // "courseInstructors" is every instructor/coordinator in the active tenant —
  // any of them can be assigned to a session opening, not just the class's
  // lead instructor — used by CoordinatorInstructorsTab and CoordinatorSessionsTab
  // to resolve names for filled openings and to populate assign-instructor dropdowns.
  React.useEffect(() => {
    if (!activeTenantId) { setCourseInstructors([]); return; }
    fetchUsers()
      .then((all) => {
        if (!Array.isArray(all)) { setCourseInstructors([]); return; }
        setCourseInstructors(all.filter((u) => {
          const tr = Array.isArray(u.tenantRoles?.[activeTenantId]) ? u.tenantRoles[activeTenantId] : [];
          return u.roles?.includes("INSTRUCTOR") || u.roles?.includes("COURSE_COORDINATOR")
            || tr.includes("INSTRUCTOR") || tr.includes("COURSE_COORDINATOR");
        }));
      })
      .catch(() => setCourseInstructors([]));
  }, [activeTenantId]);

  const refreshCourse = React.useCallback(() => {
    fetchClassById(id).then((cls) => {
      setCourse(cls);
      if (cls?.tenantId) setApiTenant(String(cls.tenantId));
    });
  }, [id]);

  // Derive active tab from URL
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const lastSegment = pathSegments[pathSegments.length - 1];
  const urlRoute = lastSegment === id ? "" : lastSegment;
  const tabIndex = Math.max(0, TABS.findIndex((t) => t.route === urlRoute));

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!course) {
    return (
      <Box sx={{ p: 4 }}>
        {loadError && <Alert severity="error" sx={{ mb: 2 }}>{loadError}</Alert>}
        <Typography color="error">Course not found.</Typography>
      </Box>
    );
  }

  // ── Derived stats ────────────────────────────────────────────────────────
  const enrolledCount = course.studentCount ?? course.enrolledStudents ?? 0;
  const sessionCount = sessions.length;
  const isCompleted = course.lifecycleStatus === "COMPLETED";
  const isDraft = course.publishStatus === "DRAFT";
  // A class made from a standard course: its content is set by Training Center and cannot be changed here.
  const isStandard = Boolean(course.standardGoverned);

  const catColors = getCatColors(course.category || "", tPrimary);

  // ── Permissions ──────────────────────────────────────────────────────────
  const canEdit = (isCourseCoordinator || isAdmin) && !isCompleted;
  const canCloseClass = (isCourseCoordinator || isAdmin) && !isCompleted;

  const tabProps = {
    course,
    courseInstructors,
    sessions,
    setSessions,
    checklists,
    refreshCourse,
    canEditDetails: canEdit,
    canEditInstructors: canEdit,
    canEditSessions: canEdit,
    canEditAttendance: false,
    canRegisterStudents: canEdit,
  };

  const hasMissingSessionDates = sessions?.some((s) => !s.sessionDate);
  const hasLiveSession = sessions?.some((s) => Boolean(s.enabled));

  // ── Tab badge counts ─────────────────────────────────────────────────────
  const tabBadges = [
    enrolledCount,
    null,
    courseInstructors.length,
    sessionCount,
    documentsCount || null,
    checklistsActiveCount || null,
    testsActiveCount || null,
    vlpCount || null,
    null,
  ];

  const navToTab = (tab) =>
    navigate(tab.route ? `/course-management/${id}/${tab.route}` : `/course-management/${id}`);

  const banner = (
    <>
      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError}
        </Alert>
      )}
      {course?.sourceTemplateId && hasMissingSessionDates && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Created from template. Sessions have no dates yet — go to the <strong>Sessions</strong> tab to schedule them.
        </Alert>
      )}
      {isStandard && (
        <Alert severity="info" sx={{ mb: 2 }} data-testid="standard-class-banner">
          This class uses a standardized course{course.localLabel ? ` (${course.localLabel})` : ""}. Its sessions, skill sheets, tests and
          documents are set by Training Center and cannot be changed. You can still set the dates, rooms, instructors and seats,
          and run the class as usual.
        </Alert>
      )}
      {isCompleted && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This class is completed and read-only.
        </Alert>
      )}
      {isDraft && !isCompleted && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This class is a draft. Students can't see it in the class listing or register until you publish it.
        </Alert>
      )}
    </>
  );

  return (
    <>
      <ClassOverview
        breadcrumbLabel="Course Management"
        onBreadcrumbClick={() => navigate("/course-management")}
        title={course.title}
        chips={[
          ...(course.courseNumber ? [{ label: course.courseNumber, mono: true }] : []),
          ...(course.category ? [{
            label: course.category,
            bg: catColors.bg, color: catColors.color, border: catColors.border,
            icon: (
              <SvgIcon size={11}>
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </SvgIcon>
            ),
          }] : []),
        ]}
        status={isDraft
          ? { label: "Draft", background: "var(--color-surface-press-stronger)", color: "rgba(31,22,51,0.6)", border: "var(--color-hairline-cloud)" }
          : isCompleted
          ? { label: "Completed", background: alpha(tPrimary, 0.06), color: tPrimary, border: alpha(tPrimary, 0.10) }
          : { label: "Published", background: alpha(tSecondary, 0.10), color: tSecondary, border: alpha(tSecondary, 0.20) }}
        stats={[]}
        tabs={TABS.map((tab, i) => ({
          key: tab.route || "roster",
          label: tab.label,
          icon: tab.icon,
          live: tab.route === "sessions" ? hasLiveSession : false,
          count: tabBadges[i],
        }))}
        activeTab={TABS[tabIndex]?.route || "roster"}
        onTabChange={(key) => navToTab(TABS.find((tab) => (tab.route || "roster") === key))}
        banner={banner}
        actions={canCloseClass ? [
          // A published class is marked complete when it has finished; certificates can then be sent from the roster.
          ...(!isDraft ? [{ key: "complete", label: "Mark class complete", onClick: () => setCompleteOpen(true) }] : []),
          {
          key: "publish",
          label: isDraft ? "Publish class" : "Unpublish class",
          onClick: () => setPublishOpen(true),
        }, {
          key: "delete",
          label: "Delete class",
          onClick: () => setDeleteOpen(true),
        }] : []}
      >
      {/* ── Tab content ────────────────────────────────────────────────────── */}
      <>
        {tabIndex === 0 && <RosterTab {...tabProps} />}
        {tabIndex === 1 && <CoordinatorDetailsTab course={course} canEdit={canEdit} isDraft={isDraft} contentLocked={isStandard}
          onPublish={canCloseClass ? () => setPublishOpen(true) : undefined}
          onDelete={canCloseClass ? () => setDeleteOpen(true) : undefined} />}
        {tabIndex === 2 && <CoordinatorInstructorsTab course={course} canEdit={canEdit} courseInstructors={courseInstructors} sessions={sessions} onRefresh={refreshCourse} />}
        {tabIndex === 3 && (
          <CoordinatorSessionsTab
            sessions={sessions}
            setSessions={setSessions}
            refreshCourse={refreshCourse}
            canEdit={canEdit}
            course={course}
            courseInstructors={courseInstructors}
            enrolledCount={enrolledCount}
            contentLocked={isStandard}
          />
        )}
        {tabIndex === 4 && (
          <DocumentsTab
            sessions={sessions}
            canEdit={canEdit && !isStandard}
            onDocumentsCountChange={setDocumentsCount}
          />
        )}
        {tabIndex === 5 && (
          <SkillChecklistsTab
            checklists={checklists}
            setChecklists={setChecklists}
            sessions={sessions}
            canEdit={canEdit}
            contentLocked={isStandard}
          />
        )}
        {tabIndex === 6 && (
          <WrittenTestsTab
            course={course}
            sessions={sessions}
            tests={tests}
            setTests={setTests}
            canEdit={canEdit}
            contentLocked={isStandard}
          />
        )}
        {tabIndex === 7 && (
          <VirtualLearningTab
            sessions={sessions}
            canEdit={canEdit && !isStandard}
          />
        )}
        {tabIndex === 8 && (
          <CoordinatorReportsTab
            course={course}
            sessions={sessions}
            checklists={checklists}
            enrolledCount={enrolledCount}
          />
        )}
      </>
      </ClassOverview>
      <ConfirmDialog
        open={publishOpen}
        title={isDraft ? "Publish class?" : "Unpublish class?"}
        message={isDraft
          ? <>Students will be able to see <b>“{course.title}”</b> in the class listing and register for it.</>
          : <><b>“{course.title}”</b> will be hidden from the class listing and students can't register for it. Students already registered keep their access.</>}
        confirmLabel={isDraft ? "Publish" : "Unpublish"}
        busyLabel={isDraft ? "Publishing…" : "Unpublishing…"}
        danger={false}
        onConfirm={async () => {
          const tenant = course.tenantId || activeTenantId || effectiveUser?.tenantId;
          setCourse(await (isDraft ? publishClass : unpublishClass)(course.id, tenant));
        }}
        onClose={() => setPublishOpen(false)}
      />
      <ConfirmDialog
        open={completeOpen}
        title="Mark class complete?"
        message={<>
          <b>“{course.title}”</b> will be closed and become read-only: no more changes to its details, sessions, attendance or
          sign-offs. Instructors can then press <b>Send certificates</b> on the roster to issue certificates (and AHA eCards) to
          the students who were signed off. Sign off every student who passed before completing the class.
          {unsignedStudents && unsignedStudents.length > 0 && (
            <Box component="span" sx={{ display: "block", mt: 1.5 }}>
              <b>{unsignedStudents.length} {unsignedStudents.length === 1 ? "student hasn’t" : "students haven’t"} been signed off</b>
              {" "}and will not get certificates: {unsignedStudents.join(", ")}. If any of them passed, sign them off first.
              Only a coordinator or admin can sign off a student who passed once the class is complete.
            </Box>
          )}
        </>}
        confirmLabel="Mark complete"
        busyLabel="Completing…"
        danger={false}
        onConfirm={async () => {
          const tenant = course.tenantId || activeTenantId || effectiveUser?.tenantId;
          setCourse(await completeClass(course.id, tenant));
        }}
        onClose={() => setCompleteOpen(false)}
      />
      <ConfirmDialog
        open={deleteOpen}
        title="Delete class?"
        message={<>
          <b>“{course.title}”</b> will be permanently deleted, along with its sessions, openings, and everything assigned to it
          (tests, skill sheets, documents). This can't be undone. A class can't be deleted while students are enrolled.
        </>}
        requireText={course.title}
        confirmLabel="Delete class"
        onConfirm={async () => {
          await deleteClass(course.tenantId || activeTenantId || effectiveUser?.tenantId, course.id);
          navigate("/course-management");
        }}
        onClose={() => setDeleteOpen(false)}
      />
    </>
  );
}

